import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Learn from "@/pages/Learn";
import LessonDetail from "@/pages/LessonDetail";
import Training from "@/pages/Training";
import Mission from "@/pages/Mission";
import ScenarioLibrary from "@/pages/ScenarioLibrary";
import Replay from "@/pages/Replay";
import Analyze from "@/pages/Analyze";
import IndicatorLab from "@/pages/IndicatorLab";
import Boss4 from "@/pages/Boss4";
import RiskLab from "@/pages/RiskLab";
import RiskProfile from "@/pages/RiskProfile";
import CoAnalysis from "@/pages/CoAnalysis";
import Analyst from "@/pages/Analyst";
import Roadmap from "@/pages/Roadmap";
import Intelligence from "@/pages/Intelligence";
import Quality from "@/pages/Quality";
import Pricing from "@/pages/Pricing";
import Account from "@/pages/Account";
import Legal from "@/pages/Legal";
import PaymentSuccess from "@/pages/PaymentSuccess";
import { Signup, ForgotPassword, ResetPassword } from "@/pages/AuthPages";
import Home from "@/public/pages/Home";
import Glossary from "@/public/pages/Glossary";
import GlossaryTerm from "@/public/pages/GlossaryTerm";
import { IntelligencePublic, AnalystPublic, HowItWorks, LearnPublic, FamilyPublic, PricingPublic, PublicLegal } from "@/public/pages/subpages";
import Assess from "@/pages/Assess";
import Journal from "@/pages/Journal";
import Performance from "@/pages/Performance";
import Skills from "@/pages/Skills";
import Achievements from "@/pages/Achievements";
import MyCalls from "@/pages/MyCalls";
import FamilyWeek from "@/pages/FamilyWeek";
import Coach from "@/pages/Coach";
import Parent from "@/pages/Parent";
import SettingsPage from "@/pages/Settings";
import More from "@/pages/More";
import Family from "@/pages/Family";
import AdminOrganic from "@/pages/AdminOrganic";
import AdminComp from "@/pages/AdminComp";
import AdminSupport from "@/pages/AdminSupport";
import { InsightsIndex, InsightArticle } from "@/public/pages/Insights";
import PublicSupport from "@/public/pages/Support";
import NotFound from "@/public/pages/NotFound";

function Loading() {
  return (
    <div className="min-h-screen bg-tc-bg flex items-center justify-center">
      <div className="text-tc-coach font-mono text-sm animate-pulse-glow">Loading TRADECRAFT…</div>
    </div>
  );
}

function MissionRoute() {
  const { missionId } = useParams();
  const location = useLocation();
  return <Mission key={`${missionId}-${location.key}`} />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded && user.role === "student") return <Navigate to="/onboarding" replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  // PUBLIC ROOT ("/") — the public/private boundary. Guests get the crawlable public marketing
  // site (prerendered in production); authenticated users go into the private app (noindex).
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Home />;
}

// Shared paths that exist both as public marketing (guest) and in-app pages (authenticated).
function DualRoute({ pub, app }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? app : pub;
}

// Marketing-only paths: guests see the public page; authenticated users go to the app.
function MarketingRoute({ pub }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? <Navigate to="/dashboard" replace /> : pub;
}

// /family: guests -> public marketing; authenticated -> in-app Family manager.
// A guest opening a secure invite link is sent to login (invite preserved) and returned.
function FamilyRoute() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  if (loading) return <Loading />;
  if (user) return <Protected><Family /></Protected>;
  if (invite) {
    localStorage.setItem("tc_pending_invite", invite);
    return <Navigate to="/login" replace />;
  }
  return <FamilyPublic />;
}

function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/how-it-works" element={<MarketingRoute pub={<HowItWorks />} />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/glossary/:term" element={<GlossaryTerm />} />
            <Route path="/family" element={<FamilyRoute />} />
            <Route path="/pricing" element={<DualRoute pub={<PricingPublic />} app={<Protected><Pricing /></Protected>} />} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/legal" element={<DualRoute pub={<PublicLegal />} app={<Protected><Legal /></Protected>} />} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/training" element={<Protected><Training /></Protected>} />
            <Route path="/training/:missionId" element={<Protected><MissionRoute /></Protected>} />
            <Route path="/learn" element={<DualRoute pub={<LearnPublic />} app={<Protected><Learn /></Protected>} />} />
            <Route path="/learn/:lessonId" element={<Protected><LessonDetail /></Protected>} />
            <Route path="/replay" element={<Protected><Replay /></Protected>} />
            <Route path="/analyze" element={<Protected><Analyze /></Protected>} />
            <Route path="/indicator-lab" element={<Protected><IndicatorLab /></Protected>} />
            <Route path="/boss4" element={<Protected><Boss4 /></Protected>} />
            <Route path="/risk-lab" element={<Protected><RiskLab /></Protected>} />
            <Route path="/risk-profile" element={<Protected><RiskProfile /></Protected>} />
            <Route path="/co-analysis" element={<Protected><CoAnalysis /></Protected>} />
            <Route path="/analyst" element={<DualRoute pub={<AnalystPublic />} app={<Protected><Analyst /></Protected>} />} />
            <Route path="/roadmap" element={<Protected><Roadmap /></Protected>} />
            <Route path="/intelligence" element={<DualRoute pub={<IntelligencePublic />} app={<Protected><Intelligence /></Protected>} />} />
            <Route path="/quality" element={<Protected><Quality /></Protected>} />
            <Route path="/assess" element={<Protected><Assess /></Protected>} />
            <Route path="/scenarios" element={<Protected><ScenarioLibrary /></Protected>} />
            <Route path="/journal" element={<Protected><Journal /></Protected>} />
            <Route path="/performance" element={<Protected><Performance /></Protected>} />
            <Route path="/skills" element={<Protected><Skills /></Protected>} />
            <Route path="/achievements" element={<Protected><Achievements /></Protected>} />
            <Route path="/my-calls" element={<Protected><MyCalls /></Protected>} />
            <Route path="/family/week" element={<Protected><FamilyWeek /></Protected>} />
            <Route path="/coach" element={<Protected><Coach /></Protected>} />
            <Route path="/parent" element={<Protected><Parent /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/more" element={<Protected><More /></Protected>} />
            <Route path="/insights" element={<InsightsIndex />} />
            <Route path="/support" element={<PublicSupport />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/admin/organic" element={<Protected><AdminOrganic /></Protected>} />
            <Route path="/admin/comp" element={<Protected><AdminComp /></Protected>} />
            <Route path="/admin/support" element={<Protected><AdminSupport /></Protected>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
